import { useRef, useState } from "react";
import { endpoints } from "../../hooks/endpoints";
import {
  getToken,
  isEmailValid,
  isPhoneValid,
  isTutorNameValid,
} from "../../hooks/utils";
import { TutorFormDataI } from "./TutorRegistration";

export const tutorSubjects = [
  { key: "ar", name: "اللغة العربية" },
  { key: "en", name: "اللغة الإنجليزية" },
  { key: "fr", name: "اللغة الفرنسية" },
  { key: "it", name: "اللغة الإيطالية" },
  { key: "du", name: "اللغة الألمانية" },
  { key: "physics-ar", name: "الفيزياء" },
  { key: "physics-en", name: "Physics" },
  { key: "chemistry-ar", name: "الكيمياء" },
  { key: "chemistry-en", name: "Chemistry" },
  { key: "biology-ar", name: "الأحياء" },
  { key: "biology-en", name: "Biology" },
  { key: "maths-ar", name: "الرياضيات" },
  { key: "maths-en", name: "Maths" },
  { key: "science-ar", name: "العلوم" },
  { key: "science-en", name: "Science" },
  { key: "geology", name: "الجيولوجيا" },
  { key: "social_studies", name: "الدراسات الإجتماعية" },
  { key: "geography", name: "الجغرافيا" },
  { key: "history", name: "التاريخ" },
  { key: "philosophy", name: "الفلسفة و المنطق وعلم النفس" },
  { key: "other", name: "اخرى" },
];

const TutorRegistrationStepOne = ({
  formData,
  handleChange,
  handleNext,
  setFormData,
}: {
  formData: TutorFormDataI;
  handleChange: (e: any) => void;
  handleNext: () => void;
  setFormData: React.Dispatch<React.SetStateAction<TutorFormDataI>>;
}) => {
  const nameRef = useRef<any>(null);
  const emailRef = useRef<any>(null);
  const phoneRef = useRef<any>(null);
  const whatsappRef = useRef<any>(null);
  const subjectRef = useRef<any>(null);
  const otherSubjectRef = useRef<any>(null);
  const otherSubjectLangRef = useRef<any>(null);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");

  const handleFirstStep = async (e) => {
    e.preventDefault();
    try {
      const {
        name,
        phone,
        whatsapp,
        email,
        subject,
        other_subject,
        other_subject_lang,
      } = formData;

      if (nameRef.current) {
        const checkName = isTutorNameValid(name);
        if (!checkName.isValid) {
          nameRef.current.style.border = "1px solid red";
          return setError(checkName.message);
        } else {
          nameRef.current.style = "";
        }
      }

      if (phoneRef.current) {
        const checkPhone = isPhoneValid(phone);
        if (!checkPhone.isValid) {
          phoneRef.current.style.border = "1px solid red";
          return setError(checkPhone.message);
        } else {
          phoneRef.current.style = "";
        }
      }

      if (whatsappRef.current) {
        const checkWhatsapp = isPhoneValid(whatsapp);
        if (!checkWhatsapp.isValid) {
          whatsappRef.current.style.border = "1px solid red";
          return setError(checkWhatsapp.message);
        } else {
          whatsappRef.current.style = "";
        }
      }

      if (emailRef.current) {
        const checkEmail = isEmailValid(email);
        if (!checkEmail.isValid) {
          emailRef.current.style.border = "1px solid red";
          return setError(checkEmail.message);
        } else {
          emailRef.current.style = "";
        }
      }

      if (subjectRef.current) {
        if (!subjectRef.current?.value) {
          subjectRef.current.style.border = "1px solid red";
          return setError("يرجى إختيار المادة الدراسية");
        } else {
          subjectRef.current.style = "";
        }
      }

      if (!name || !phone || !whatsapp || !email || !subject) {
        return setError("يرجى تعبئة جميع الحقول");
      }

      if (
        subject === "other" &&
        otherSubjectRef.current &&
        otherSubjectLangRef.current
      ) {
        if (!other_subject) {
          otherSubjectRef.current.style.border = "1px solid red";
          return setError("يرجى كتابة اسم المادة");
        } else {
          otherSubjectRef.current.style = "";
        }

        if (!other_subject_lang) {
          otherSubjectLangRef.current.style.border = "1px solid red";
          return setError("يرجى رختيار لغة المادة");
        } else {
          otherSubjectLangRef.current.style = "";
        }
      }

      setIsPending(true);
      setError("");

      console.log(formData);

      const response = await fetch(endpoints.verifyTutorData, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        body: JSON.stringify(formData),
      });
      const res = await response.json();

      if (response.ok && res?.message === "data_valid") {
        setFormData((prev) => ({
          ...prev,
          is_new: res?.data?.key === "is_new",
        }));
        handleNext();
      } else {
        setError(res?.data?.message);
      }
    } catch (error) {
      setError("حدث خطأ");
      console.error("Error:", error);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form onSubmit={handleFirstStep} className="space-y-4">
      <h4 className="mx-auto mb-6 pb-1 text-xl font-semibold border-b-2 border-(--primary-color-light) w-fit">
        البيانات الشخصية للمدرس
      </h4>

      <div>
        <label className="block font-bold mb-1">الإسم باللغة العربية</label>
        <input
          style={{ direction: "rtl" }}
          ref={nameRef}
          type="text"
          name="name"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
          value={formData.name}
          onChange={handleChange}
          disabled={isPending}
        />
      </div>

      <div>
        <label className="block font-bold mb-1">رقم الهاتف</label>
        <input
          style={{ direction: "rtl" }}
          ref={phoneRef}
          type="tel"
          name="phone"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
          value={formData.phone}
          onChange={handleChange}
          disabled={isPending}
        />
      </div>

      <div>
        <label className="block font-bold mb-1">رقم الواتساب</label>
        <input
          style={{ direction: "rtl" }}
          ref={whatsappRef}
          type="tel"
          name="whatsapp"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
          value={formData.whatsapp}
          onChange={handleChange}
          disabled={isPending}
        />
      </div>

      <div>
        <label className="block font-bold mb-1">الإيميل</label>
        <input
          style={{ direction: "rtl" }}
          ref={emailRef}
          type="email"
          name="email"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
          value={formData.email}
          onChange={handleChange}
          disabled={isPending}
        />
      </div>

      <div className="mt-6">
        <label className="block font-bold mb-1">المادة الدراسية</label>
        <select
          ref={subjectRef}
          name="subject"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
          value={formData.subject}
          onChange={handleChange}
          disabled={isPending}
        >
          <option disabled value="">
            اختر المادة
          </option>
          {tutorSubjects.map((subject) => (
            <option key={subject.key} value={subject.key}>
              {subject.name}
            </option>
          ))}
        </select>
      </div>

      {formData.subject === "other" && (
        <>
          <div>
            <label className="block font-bold mb-1">أكتب اسم المادة</label>
            <input
              style={{ direction: "rtl" }}
              ref={otherSubjectRef}
              type="text"
              name="other_subject"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
              value={formData.other_subject}
              onChange={handleChange}
              disabled={isPending}
            />
          </div>

          <div className="mt-6">
            <label className="block font-bold mb-1">لغة المادة</label>
            <select
              ref={otherSubjectLangRef}
              name="other_subject_lang"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-primary"
              value={formData.other_subject_lang}
              onChange={handleChange}
              disabled={isPending}
            >
              <option disabled value="">
                اختر اللغة
              </option>
              <option value="ar">اللغة العربية</option>
              <option value="en">اللغة الإنجليزية</option>
              <option value="fr">اللغة الفرنسية</option>
              <option value="it">اللغة الإيطالية</option>
              <option value="du">اللغة الألمانية</option>
            </select>
          </div>
        </>
      )}

      <button
        type="submit"
        disabled={isPending}
        className="mt-6 w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-(--primary-color-dark) transition-all disabled:opacity-50"
      >
        {isPending ? "التالي ..." : "التالي"}
      </button>

      {!!error && (
        <div className="mt-4 w-full text-center bg-red-50 text-red-500 border border-red-400 py-2 px-4 rounded-md">
          {error}
        </div>
      )}
    </form>
  );
};

export default TutorRegistrationStepOne;
